import { useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { revenueCat } from "../../../lib/purchases/revenueCat";

/**
 * Alinha o SDK do RevenueCat ao usuário logado — e só isso.
 *
 * **Não mostra nada e não compra nada.** A UI de compra continua atrás de
 * `purchaseAvailability.ts`, que responde `false` no nativo. Este componente
 * existe porque o Play Console só libera a criação de assinaturas depois que
 * um build declarar `com.android.vending.BILLING`, e a permissão viaja com o
 * plugin — ou seja, a fundação precisa ir ao ar antes da vitrine.
 *
 * ── Por que é um componente, e por que mora ao lado do FcmTokenSync ─────────
 *
 * A identidade que o RevenueCat precisa (`appUserID = uid`) só existe DEPOIS
 * que o Firebase resolve a sessão, e o único lugar que sabe disso é o
 * `AuthContext`. Um `configure()` no `main.tsx` rodaria cedo demais e teria
 * de inventar um id — que é exatamente o que o webhook recusa (`$RCAnonymousID`).
 *
 * É o mesmo desenho do `FcmTokenSync`, pela mesma razão: efeito colateral
 * global que depende de auth, montado uma vez, sem UI.
 *
 * ── Web ─────────────────────────────────────────────────────────────────────
 *
 * Em build web o `sync()` sai no guard de plataforma antes de qualquer
 * `import()`, então o plugin não entra no bundle nem é avaliado. Montar este
 * componente na web custa uma chamada de função que retorna `"skipped-web"`.
 */
export function RevenueCatSync() {
  const { user, isLoading } = useAuth();
  const uid = user?.uid ?? null;

  useEffect(() => {
    // Enquanto a sessão não resolveu, `uid` é `null` por não sabermos ainda —
    // e não por a pessoa estar deslogada. Agir aqui dispararia um `logOut()`
    // no SDK a cada boot de usuário logado, entre o primeiro render e o
    // callback do Firebase.
    if (isLoading) return;
    void revenueCat.sync(uid);
  }, [uid, isLoading]);

  return null;
}
